import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// Mirror of types in apps/api/src/services/account-duplicates.service.ts.
export interface DuplicateEntrySide {
  journalEntryId: string;
  entryNumber: string;
  date: string;
  memo: string | null;
  source: string;
  amount: string;
  side: 'debit' | 'credit';
  isLocked: boolean;
  lockedByReconciliationId: string | null;
}

export interface DuplicatePair {
  key: string;
  left: DuplicateEntrySide;
  right: DuplicateEntrySide;
}

export interface FindDuplicatesResult {
  leftAccount: { id: string; code: string; name: string };
  rightAccount: { id: string; code: string; name: string };
  pairs: DuplicatePair[];
  unmatchedLeft: DuplicateEntrySide[];
  unmatchedRight: DuplicateEntrySide[];
}

export interface VoidBulkResult {
  succeeded: string[];
  failed: { journalEntryId: string; reason: string }[];
}

export interface MoveLinesBulkResult {
  succeeded: { journalEntryId: string; linesMoved: number }[];
  failed: { journalEntryId: string; reason: string }[];
}

export function useAccountDuplicates(
  companyId: string | null,
  leftAccountId: string | null,
  rightAccountId: string | null,
  opts: { dateFrom?: string; dateTo?: string } = {},
) {
  return useQuery({
    queryKey: [
      'account-duplicates',
      companyId ?? 'none',
      leftAccountId ?? 'none',
      rightAccountId ?? 'none',
      opts.dateFrom ?? '',
      opts.dateTo ?? '',
    ] as const,
    enabled:
      companyId !== null &&
      !!leftAccountId &&
      !!rightAccountId &&
      leftAccountId !== rightAccountId,
    queryFn: () => {
      const params = new URLSearchParams({
        leftAccountId: leftAccountId!,
        rightAccountId: rightAccountId!,
      });
      if (opts.dateFrom) params.set('dateFrom', opts.dateFrom);
      if (opts.dateTo) params.set('dateTo', opts.dateTo);
      return api
        .get<{ result: FindDuplicatesResult }>(
          `/companies/${companyId}/accounts/duplicates?${params}`,
        )
        .then((r) => r.result);
    },
    staleTime: 30_000,
  });
}

/**
 * Bulk re-point every line on `sourceAccountId` to `targetAccountId` for
 * the given journal entries. Used by the Find Duplicates "Move to other
 * account" action when the source-account entries don't have a duplicate
 * on the target account yet.
 */
export function useMoveJournalLinesBulk(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      journalEntryIds,
      sourceAccountId,
      targetAccountId,
    }: {
      journalEntryIds: string[];
      sourceAccountId: string;
      targetAccountId: string;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: MoveLinesBulkResult }>(
          `/companies/${companyId}/journal-entries/move-lines-bulk`,
          { journalEntryIds, sourceAccountId, targetAccountId },
        )
        .then((r) => r.result);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-duplicates', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}

export function useVoidJournalEntriesBulk(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      journalEntryIds,
      voidReason,
    }: {
      journalEntryIds: string[];
      voidReason: string;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: VoidBulkResult }>(
          `/companies/${companyId}/journal-entries/void-bulk`,
          { journalEntryIds, voidReason },
        )
        .then((r) => r.result);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-duplicates', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
    },
  });
}
